import { useMemo, useState } from "react"
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material"
import { CustomTable } from "@/components/Tables/CustomTable"
import { columns } from "@/pages/orders/Columns"
import Modal from "./Model"
import {
  OrderCrudProvider,
  useOrderCrudContext,
} from "@/context/orderCrudContext"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { apiFetch } from "@/hooks/apiFetch"
import useSetting from "@/hooks/useSetting"
import usePaymentMethods from "@/hooks/usePaymentMethods"
import { useAppSelector } from "@/lib/hooks"
import { isAdminPortal } from "@/utilities/domain"
import {
  ReportBranchFilter,
  ReportBranchProvider,
  useReportBranch,
  withBranchColumn,
} from "@/components/Reports/ReportBranchFilter"

const POS_ORDER_FILTERS = { type: "pos" }
const adminColumns = withBranchColumn(columns)

export default function OrderList() {
  return (
    <>
      <div className="breadcrumb-card">
        <h5>Sales</h5>
      </div>

      {isAdminPortal() ? (
        <ReportBranchProvider>
          <AdminOrderTable />
        </ReportBranchProvider>
      ) : (
        <OrderCrudProvider>
          <OrderTable />
        </OrderCrudProvider>
      )}
    </>
  )
}

function OrderTable() {
  const permissions = new Set(
    useAppSelector((state) => state.auth.user?.permissions ?? [])
  )
  const { openOrderDialog, setOpenOrderDialog, setSelectedOrderRow } =
    useOrderCrudContext()
  const navigate = useNavigate()
  const [paymentStatusId, setPaymentStatusId] = useState("")
  const [salesStatusId, setSalesStatusId] = useState("")
  const [paymentMethodId, setPaymentMethodId] = useState("")
  const { settingData: paymentStatusData } = useSetting("payment-status", "asc")
  const { settingData: salesStatusData } = useSetting("sales-status", "asc")
  const { paymentMethods } = usePaymentMethods()
  const salesFilters = useMemo(
    () => ({
      ...POS_ORDER_FILTERS,
      ...(paymentStatusId && { payment_status_id: paymentStatusId }),
      ...(salesStatusId && { sales_status_id: salesStatusId }),
      ...(paymentMethodId && { payment_method_id: paymentMethodId }),
    }),
    [paymentStatusId, salesStatusId, paymentMethodId]
  )

  return (
    <>
      <CustomTable
        label="Sales"
        columns={columns}
        apiPath="order"
        perPage={60}
        hardcodedFilters={salesFilters}
        onAdd={() => setOpenOrderDialog(true)}
        onView={(rowData) => {
          navigate(`/pos/sales/${rowData.id}`)
        }}
        onEdit={async (rowData) => {
          try {
            const order = await apiFetch(`order/${rowData.id}`)
            setSelectedOrderRow(order.data)
            setOpenOrderDialog(true)
          } catch (error) {
            toast.error(error.message)
          }
        }}
        addBtn={permissions.has("sales-orders-create")}
        showEdit={permissions.has("sales-orders-update")}
        showDelete={permissions.has("sales-orders-delete")}
        onExport={permissions.has("operations-exports-create")}
        showImport={
          permissions.has("operations-imports-create") &&
          permissions.has("sales-orders-create")
        }
        renderFilters={() => (
          <div className="flex flex-wrap gap-2">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="sales-payment-status-filter-label">
                Payment status
              </InputLabel>
              <Select
                labelId="sales-payment-status-filter-label"
                label="Payment status"
                value={paymentStatusId}
                onChange={(event) => setPaymentStatusId(event.target.value)}
              >
                <MenuItem value="">All payment statuses</MenuItem>
                {paymentStatusData?.data?.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    {status.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="sales-sales-status-filter-label">
                Sales status
              </InputLabel>
              <Select
                labelId="sales-sales-status-filter-label"
                label="Sales status"
                value={salesStatusId}
                onChange={(event) => setSalesStatusId(event.target.value)}
              >
                <MenuItem value="">All sales statuses</MenuItem>
                {salesStatusData?.data?.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    {status.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="sales-payment-method-filter-label">
                Payment method
              </InputLabel>
              <Select
                labelId="sales-payment-method-filter-label"
                label="Payment method"
                value={paymentMethodId}
                onChange={(event) => setPaymentMethodId(event.target.value)}
              >
                <MenuItem value="">All payment methods</MenuItem>
                <MenuItem value="multiple">Multiple methods</MenuItem>
                {paymentMethods?.data?.map((method) => (
                  <MenuItem key={method.id} value={method.id}>
                    {method.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}
      />

      {openOrderDialog ? <Modal /> : null}
    </>
  )
}

// Company administrators review every branch's sales, or a selected one;
// sales are recorded on the branch portals. Payment and sales statuses are
// branch settings, so only the branch filter applies here.
function AdminOrderTable() {
  const navigate = useNavigate()
  const { branchId } = useReportBranch()
  const filters = useMemo(
    () => ({ ...POS_ORDER_FILTERS, branch_id: branchId }),
    [branchId]
  )
  const exportParams = useMemo(() => ({ branch_id: branchId }), [branchId])

  return (
    <CustomTable
      label="Sales"
      columns={adminColumns}
      apiPath="order"
      perPage={60}
      hardcodedFilters={filters}
      onView={(rowData) => navigate(`/pos/sales/${rowData.id}`)}
      addBtn={false}
      showEdit={false}
      showDelete={false}
      onExport
      exportParams={exportParams}
      renderFilters={() => <ReportBranchFilter sx={{ mb: 0 }} />}
    />
  )
}
